export type UserRole = 'standard' | 'admin' | 'super_admin' | 'leader' | 'manager';

/**
 * 检查是否具有管理员角色（包括普通管理员和超级管理员）
 */
export function hasAdminRole(role: string | undefined | null): boolean {
  return role === 'admin' || role === 'super_admin';
}

export type AuthMethod = 'mobile_code' | 'wechat_qr' | 'wechat_mobile' | 'password';

export type UserAccountStatus = 'normal' | 'disabled' | 'pending_deletion' | 'deleted';

export interface UserAccount {
  id: string;
  mobile: string;
  wechatOpenId?: string;
  displayName: string;
  /**
   * 头像可访问地址：由登录/`/me`/资料接口现签的预签名 URL（有效期约 4 小时），
   * 不是库里的 object key —— 顶部栏与侧边栏直接用它渲染 <img>。
   * 未设置头像时后端返回 null。
   */
  avatarUrl?: string;
  organizationId?: string;
  organizationName?: string;
  organizationType?: string;
  region?: string;
  role: UserRole;
  /**
   * 细粒度权限码（如 admin:role:view、workspace:report:create），由后端登录/`/me` 接口下发，
   * 口径与后端 SecurityContext authorities 一致。为空时权限判定降级到角色硬编码兜底。
   */
  permissions?: string[];
  status?: UserAccountStatus;
  /** 是否已设置密码（后端脱敏口径，绝不下发 hash 原值） */
  passwordSet?: boolean;
  isFirstLogin: boolean;
  forcePasswordChange?: boolean;
  createdAt: Date;
  /** 从未登录时为 undefined（用于"从未登录"文案判断） */
  lastLoginAt?: Date;
}

export interface LoginSession {
  sessionId: string;
  userId: string;
  token: string;
  createdAt: Date;
  expiresAt: Date;
  deviceInfo?: string;
}

export interface VerificationCode {
  mobile: string;
  code: string;
  createdAt: Date;
  expiresAt: Date;
  attemptCount: number;
}

export interface AuthState {
  currentUser: UserAccount | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export type LoginParams =
  | { method: 'mobile_code'; mobile: string; code: string }
  | { method: 'wechat_qr'; authCode: string; state?: string }
  | { method: 'wechat_mobile'; authCode: string; state?: string }
  | { method: 'password'; username: string; password: string };

export interface LoginResult {
  success: boolean;
  user?: UserAccount;
  token?: string;
  refreshToken?: string;  // 新增 - Refresh Token
  expiresIn?: number;     // 新增 - Access Token 过期秒数
  redirectTo?: string;
  requireSetup?: boolean;
  error?: string;
}

export interface SendCodeResult {
  success: boolean;
  message?: string;
  error?: string;
}
